import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Phone,
  Package,
  Truck,
  Luggage,
  ShoppingBag,
  Store,
  UtensilsCrossed,
  Check,
  ArrowUpRight,
  ArrowRight,
} from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingCTA from "@/components/site/FloatingCTA";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT } from "@/data/content";

const heroImage =
  "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMGFpcnBsYW5lJTIwbG9hZGluZ3xlbnwwfHx8fDE3ODM4OTYwMDN8MA&ixlib=rb-4.1.0&q=85";

const KEYWORD_ALIASES = [
  {
    cluster: "Japan to India Courier Service",
    icon: "📦",
    slug: "courier",
    items: [
      { term: "Japan to India courier service", ja: "" },
      { term: "Cheapest courier from Japan to India", ja: "格安国際宅配便" },
      { term: "Send parcel from Japan to India", ja: "" },
      { term: "DHL Japan to India rates (compare)", ja: "" },
      { term: "Express courier Japan to India", ja: "" },
      { term: "Japan to India cargo service", ja: "" },
    ],
  },
  {
    cluster: "International Moving & Relocation",
    icon: "🏠",
    slug: "moving",
    items: [
      { term: "International moving Japan to India", ja: "" },
      { term: "Relocation services Japan to India", ja: "" },
      { term: "Moving from Tokyo to India", ja: "" },
      { term: "Packers and movers Japan to India", ja: "" },
      { term: "Sea freight movers Japan to India", ja: "" },
      { term: "Luggage shipping Japan to India", ja: "" },
    ],
  },
  {
    cluster: "E-Commerce Logistics & Cross-Border B2C",
    icon: "💻",
    slug: "ecommerce",
    items: [
      { term: "Japan to India ecommerce shipping", ja: "" },
      { term: "Shopify shipping Japan to India", ja: "" },
      { term: "Amazon Japan to India delivery", ja: "" },
      { term: "Ecommerce fulfillment Japan India", ja: "" },
      { term: "Japan to India 3PL logistics", ja: "" },
      { term: "Customs clearance ecommerce India", ja: "" },
    ],
  },
  {
    cluster: "Japanese Food Import to India",
    icon: "🍱",
    slug: "food",
    items: [
      { term: "Send Japanese food to India", ja: "日本の食品をインドへ" },
      { term: "Import Japanese food to India", ja: "" },
      { term: "Ship matcha to India", ja: "抹茶をインドへ発送" },
      { term: "Sake shipping Japan to India", ja: "" },
      { term: "Japanese ramen import India", ja: "" },
      { term: "FSSAI Japanese food import", ja: "" },
      { term: "Japanese snacks Amazon India", ja: "" },
      { term: "Japanese food e-commerce India", ja: "" },
    ],
  },
];

const INTENTS = [
  {
    id: "courier",
    icon: Package,
    tag: "01 · COURIER",
    title: "Courier from Japan to India",
    ja: "日本からインドへの国際宅配便",
    body:
      "Send documents, business samples, gifts, and small parcels from any Japanese city — Tokyo, Osaka, Nagoya, Fukuoka, Sapporo — to any Indian pincode. Air-express clearance at Delhi (DEL) in under 6 hours. Signed digital POD returned to your inbox within 6 hours of delivery.",
    bullets: [
      "Pickup from any Japanese postal code (via partner network)",
      "Bill-of-Entry / Courier BE filing on your behalf",
      "GST-inclusive duty paid on our credit, invoiced to you",
      "Real-time WhatsApp tracking per HAWB",
    ],
    priceHint: "₹1,200 per kg — door-to-door, customs duty included, 5-day delivery across India",
  },
  {
    id: "moving",
    icon: Truck,
    tag: "02 · INTERNATIONAL MOVING",
    title: "International moving Japan to India",
    ja: "日本からインドへの引越し・移転サービス",
    body:
      "Complete household relocation from Japan to any Indian city under Transfer of Residence (TR) — used furniture, appliances, kitchenware, personal effects, and books. Sea container or air freight, our choice depending on your timeline. Customs cleared under Baggage Rules 2016 with concessional duty for qualifying returnees.",
    bullets: [
      "TR duty concession filed on your behalf",
      "Sea (NRT/YOK → NSA/JNPT) or air (NRT/HND → DEL/BOM/BLR/MAA)",
      "Bonded warehousing at Greater Noida if you land before cargo",
      "Door delivery to any Indian pincode",
    ],
    priceHint: "₹1,200 per kg all-inclusive by air · sea freight quoted per CBM (WhatsApp for a firm rate)",
  },
  {
    id: "luggage",
    icon: Luggage,
    tag: "03 · UNACCOMPANIED BAGGAGE",
    title: "Luggage & personal effects from Japan to India",
    ja: "日本からインドへの別送品・パーソナルエフェクト通関",
    body:
      "Shipped separately from the passenger. Suitcases, boxes, personal items, hobby equipment, and gifts. Cleared under the Baggage Rules 2016 unaccompanied baggage regime — must land within the permitted window (typically two months before / one month after the passenger's arrival).",
    bullets: [
      "Passport + Japanese residence proof coordination",
      "Detailed packing list valued in INR",
      "Bill-of-Entry filed by our licensed CHA",
      "Door delivery to home address in India",
    ],
    priceHint: "₹1,200 per kg — door-to-door, TR customs duty included, 5-day India delivery",
  },
  {
    id: "ecommerce",
    icon: ShoppingBag,
    tag: "04 · E-COMMERCE / D2C",
    title: "E-commerce goods Japan → India",
    ja: "日本発インド向け越境ECの通関と配送",
    body:
      "Cross-border D2C brands and marketplace sellers shipping consumer electronics, cosmetics, fashion, hobby goods, or beauty products to Indian end-customers. Consolidations filed under Courier Bill of Entry (fastest path), sorted at our Greater Noida bonded hub, and dispatched to 19,000+ Indian pincodes with signed digital POD.",
    bullets: [
      "Batched Courier Bill of Entry for consolidations",
      "BIS + WPC compliance validated at pre-alert",
      "Pincode sortation at our bonded hub",
      "COD, reverse logistics, and RTO handling",
    ],
    priceHint: "₹1,200 per kg all-inclusive · monthly-volume slab discounts on request",
  },
  {
    id: "dropshipping",
    icon: Store,
    tag: "05 · DROPSHIPPING",
    title: "Dropshipping from Japan to India",
    ja: "日本からインドへのドロップシッピング物流",
    body:
      "Dropshippers selling Japanese products (electronics, hobby, anime merchandise, health products, beauty) to Indian consumers on Shopify, Amazon.in, Flipkart, Meesho, or via their own storefronts. We handle bulk import consolidation, GST-compliant last-mile invoicing to end-customers, and returns processing — so you focus on marketing while we operate the India logistics stack.",
    bullets: [
      "Bulk consolidation from Japanese suppliers to Greater Noida hub",
      "GST-registered last-mile invoicing to Indian end-customer",
      "Returns / RTO / refunds handled at our India warehouse",
      "Marketplace-ready POD + delivery proof for Amazon/Flipkart claims",
    ],
    priceHint: "₹1,200 per kg all-inclusive · volume-slab discounts for dropshippers",
  },
  {
    id: "food",
    icon: UtensilsCrossed,
    tag: "06 · JAPANESE FOOD IMPORT",
    title: "Send Japanese food to India",
    ja: "日本の食品をインドへ送る・輸入する",
    body:
      "Matcha, sake, ramen, dashi, miso, soy sauce, seaweed, wagashi, Japanese Kit-Kat, Pocky, senbei, and any other packaged Japanese food to India. Every food consignment cleared with FSSAI compliance validated at pre-alert — the exact step Japan Post EMS cannot do, which is why food parcels via EMS get held for 10–20 days at India Post's FPO.",
    bullets: [
      "FSSAI licence + Import Product Approval pre-validated",
      "HSN classification for matcha, sake, ramen, snacks handled in-house",
      "Cold-chain preservation for perishable Japanese food",
      "Amazon.in / Flipkart / Shopify India marketplace-ready POD",
    ],
    priceHint: "₹1,200 per kg all-inclusive · FSSAI compliance included · 5-day India delivery",
  },
];

const FAQS = [
  {
    q: "Do I need an import licence (IEC / FSSAI / BIS) to receive goods from Japan in India?",
    a: "No. Custom Clearance Partner handles all import licences on our end — you do not need an IEC (Importer-Exporter Code), FSSAI licence, BIS registration, or WPC clearance yourself. We import against our own licences and deliver to your Indian address as a private consignee. This is why we're the go-to option for individuals, dropshippers, first-time importers, and small e-commerce sellers who don't want the overhead of acquiring Indian import licences.",
  },
  {
    q: "What is the cheapest courier from Japan to India?",
    a: "The cheapest courier from Japan to India is typically an economy air-express service via NRT, HND, or KIX — starting around ¥3,500–¥5,500 for small documents or parcels under 1 kg. Custom Clearance Partner compares DHL, FedEx, and dedicated air-express carrier rates on your behalf and quotes the lowest legal option including all Indian customs duty and last-mile delivery. WhatsApp us the parcel details for a firm quote in 15 minutes.",
  },
  {
    q: "How do I send a parcel from Japan to India?",
    a: "To send a parcel from Japan to India: (1) share the origin postal code, cargo description, weight, dimensions, and Indian destination pincode with our desk, (2) we book pickup via our Japanese courier partner, (3) we handle Bill-of-Entry filing and Indian customs clearance at IGI Delhi, and (4) door delivery to the Indian address with signed digital POD. Full cycle typically 3–5 days door-to-door.",
  },
  {
    q: "How does Japan to India express courier compare with DHL Japan to India rates?",
    a: "For high-value or time-critical shipments, DHL Japan-India rates are competitive but often include opaque surcharges (fuel, remote-area, duty processing). Custom Clearance Partner as a licensed Indian CHA quotes an all-inclusive Japan-to-India rate — pickup + international air + Indian customs duty + last-mile — with no surprise line items. For dropshipping and e-commerce lanes, we consistently underprice integrator rates by 15–30%.",
  },
  {
    q: "What is the international moving Japan to India process for household goods?",
    a: "International moving from Japan to India involves: (1) survey and packing at your Japanese residence via a partner packer, (2) sea container (BOM/JNPT) or air freight (DEL/BOM/BLR/MAA) depending on urgency, (3) Transfer of Residence (TR) Bill-of-Entry filing under Baggage Rules 2016 with concessional duty for qualifying returnees, (4) bonded warehousing at Greater Noida hub, and (5) door delivery + unpacking at your Indian address. Custom Clearance Partner handles steps 3–5 as a licensed India CHA.",
  },
  {
    q: "Are there packers and movers Japan to India that also handle customs?",
    a: "Yes. Traditional packers-and-movers companies handle only origin-side packing and transport. Custom Clearance Partner integrates with Japanese packers and adds the licensed India-side CHA function — filing, duty payment on our credit, and last-mile delivery — so you deal with one point of contact for the entire Japan-to-India move.",
  },
  {
    q: "How much does moving from Tokyo to India cost for a family of 4?",
    a: "A typical Tokyo-to-India move for a 4-person family (used household articles, 20-25 CBM) via sea freight costs approximately ¥400,000–¥750,000 including origin packing, international transport, TR customs clearance in India, and door delivery. Cost varies by exact CBM, destination Indian city, and whether it's air or sea freight. WhatsApp us with your CBM estimate for a firm quote.",
  },
  {
    q: "How do I ship luggage from Japan to India as unaccompanied baggage?",
    a: "Luggage shipping from Japan to India as unaccompanied baggage is filed under Baggage Rules 2016. Requirements: passport with two-year Japan residence proof, Japanese employer / residence certificate, detailed packing list valued in INR, and transport document (HAWB or ocean B/L). Cargo must land within permitted window (typically two months before / one month after passenger's arrival). Custom Clearance Partner files the Bill of Entry, pays duty, and delivers door-to-door.",
  },
  {
    q: "What sea freight movers Japan to India do you work with?",
    a: "We coordinate with leading NVOCC and shipping-line partners on the Yokohama-Nhava Sheva, Kobe-Nhava Sheva, and Tokyo-Mundra lanes for full-container and less-than-container loads. Custom Clearance Partner handles the India-side clearance and last-mile — the Japanese-side booking is done in partnership with your preferred origin agent or via our recommended origin partners.",
  },
  {
    q: "How does Japan to India ecommerce shipping work for Shopify or Amazon Japan sellers?",
    a: "For Shopify shipping Japan to India, Amazon Japan to India delivery, or any cross-border B2C store: we consolidate parcels from Japanese suppliers, file batched Courier Bills of Entry at IGI Delhi (fastest legal path for e-commerce), sort by destination pincode at our Greater Noida bonded hub, and dispatch to end-customers with GST-compliant invoicing. Returns are processed at our India warehouse and reconciled monthly.",
  },
  {
    q: "Do you offer ecommerce fulfillment Japan India or Japan to India 3PL logistics?",
    a: "Yes. We operate a full 3PL Japan-to-India fulfillment stack: bulk import consolidation from Japanese suppliers, warehousing at our Greater Noida bonded hub, pincode-sortation, GST-registered last-mile invoicing to Indian end-customers, and returns/RTO handling. Ideal for dropshippers, cross-border D2C brands, and marketplace sellers on Amazon.in, Flipkart, Meesho, and Shopify India.",
  },
  {
    q: "How is customs clearance for ecommerce shipments from Japan to India handled?",
    a: "Customs clearance for e-commerce shipments from Japan to India is filed under the Courier Bill of Entry regime at IGI Delhi Courier Terminal, BLR Courier Terminal, or BOM Courier Terminal — the fastest legal path for consolidated low-value parcels. BIS, WPC, and CDSCO compliance references (where applicable) are pre-validated and attached via eSANCHIT before filing. Median clearance time under 6 hours.",
  },
  {
    q: "Can I ship electronics from Japan to India (BIS/WPC compliance)?",
    a: "Yes. Electronics from Japan require BIS registration (Bureau of Indian Standards) for many product categories and WPC clearance (Wireless Planning & Coordination) for wireless devices at the Indian consignee end. Custom Clearance Partner validates BIS/WPC licence references at pre-alert stage and attaches them via eSANCHIT before filing. First-time exam pass rate on Japanese electronics is above 99%.",
  },
  {
    q: "What Indian import duty applies to Japanese goods under IJCEPA?",
    a: "The India-Japan Comprehensive Economic Partnership Agreement (IJCEPA) provides concessional duty on many Japanese-origin products when a valid Certificate of Origin is attached to the Bill of Entry. We check IJCEPA eligibility per HSN at pre-alert stage and file with the concessional rate wherever applicable — often saving 5–15% on landed cost versus MFN duty.",
  },
  {
    q: "How do I track my Japan-to-India shipment?",
    a: "Every shipment is tracked at the HAWB level via WhatsApp — you receive status updates at pre-alert, filing, duty-paid, Out-of-Charge, dispatched, and delivered. Signed digital POD is returned to your inbox within 6 hours of delivery.",
  },
];

export default function JapanToIndia() {
  useEffect(() => {
    document.title =
      "Japan to India Shipping: Courier, Food (Matcha, Sake, Ramen), Moving, E-Commerce, Dropshipping · ₹1,200/kg | Custom Clearance Partner";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Ship anything from Japan to India at ₹1,200/kg all-inclusive — door-to-door, customs duty included, 5-day delivery. Courier, international moving, unaccompanied luggage, e-commerce, dropshipping, and Japanese food import (matcha, sake, ramen, snacks) with FSSAI compliance. Licensed India CHA. Pickup anywhere in Japan. WhatsApp +91 78003 20078.";
    let mk = document.querySelector('meta[name="keywords"][data-japan]');
    if (!mk) {
      mk = document.createElement("meta");
      mk.name = "keywords";
      mk.setAttribute("data-japan", "1");
      document.head.appendChild(mk);
    }
    mk.content =
      "Japan to India courier service, cheapest courier from Japan to India, send parcel from Japan to India, DHL Japan to India rates, express courier Japan to India, Japan to India cargo service, international moving Japan to India, relocation services Japan to India, moving from Tokyo to India, packers and movers Japan to India, sea freight movers Japan to India, luggage shipping Japan to India, Japan to India ecommerce shipping, Shopify shipping Japan to India, Amazon Japan to India delivery, ecommerce fulfillment Japan India, Japan to India 3PL logistics, customs clearance ecommerce India, 日本からインド 宅配便, 日本 インド 引越し";

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = "https://customclearancepartner.in/japan-to-india";

    // JSON-LD graph: Service per intent + FAQPage + BreadcrumbList + Speakable
    const id = "ccp-japan-jsonld";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://customclearancepartner.in/japan-to-india",
          name: "Japan to India Shipping — Courier, Moving, Luggage, E-Commerce, Dropshipping",
          inLanguage: "en-IN",
          about: { "@id": "https://customclearancepartner.in/#org" },
          speakable: {
            "@type": "SpeakableSpecification",
            xpath: [
              "//*[@id='japan-hero-title']",
              "//*[@id='japan-tldr']",
            ],
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://customclearancepartner.in/" },
            { "@type": "ListItem", position: 2, name: "Japan to India", item: "https://customclearancepartner.in/japan-to-india" },
          ],
        },
        ...INTENTS.map((s) => {
          const cluster = KEYWORD_ALIASES.find((k) => k.slug === s.id);
          return {
            "@type": "Service",
            name: s.title,
            alternateName: [s.ja, ...(cluster ? cluster.items.map((i) => i.term) : [])].filter(Boolean),
            serviceType: s.tag,
            description: s.body,
            areaServed: [
              { "@type": "Country", name: "Japan" },
              { "@type": "Country", name: "India" },
            ],
            provider: { "@id": "https://customclearancepartner.in/#org" },
          };
        }),
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(script);
    return () => document.getElementById(id)?.remove();
  }, []);

  return (
    <div className="grain min-h-screen bg-[#F9FAFB] text-[#101828]" data-testid="japan-to-india-page">
      <Header />

      {/* Hero */}
      <section className="border-b border-[#E4E7EC] bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <nav className="font-mono-accent flex items-center gap-3 text-[#475467]" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D92D20]">HOME</Link>
            <span>/</span>
            <span className="text-[#101828]">JAPAN → INDIA</span>
          </nav>

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="overline mb-4 flex items-center gap-3">
                <span className="text-2xl leading-none">🇯🇵 → 🇮🇳</span>
                Japan → India · 日本 → インド
              </p>
              <h1
                id="japan-hero-title"
                data-testid="japan-hero-title"
                className="font-display text-4xl font-black leading-[0.98] tracking-tight text-[#101828] sm:text-5xl md:text-6xl lg:text-7xl"
              >
                Ship anything from{" "}
                <span className="text-[#D92D20]">Japan to India.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#344054] md:text-xl">
                Courier, international moving, unaccompanied luggage, e-commerce
                goods, and dropshipping — one licensed India desk handles all
                five. Under 6-hour clearance at Delhi. Door delivery to any of
                <strong> 19,000+ Indian pincodes</strong>.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="japan-hero-whatsapp"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-[#25D366] px-6 py-4 font-semibold text-[#0B111A] shadow-hard hover:bg-[#1FBE5A]"
                >
                  <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                  WhatsApp for a quote
                </a>
                <a
                  href={CONTACT.telHref}
                  data-testid="japan-hero-call"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-white px-6 py-4 font-semibold text-[#101828] hover:bg-[#101828] hover:text-white"
                >
                  <Phone className="h-5 w-5" strokeWidth={2.5} />
                  {CONTACT.phoneDisplay}
                </a>
              </div>

              {/* No-licence differentiator */}
              <div
                data-testid="no-license-callout"
                className="mt-6 flex items-start gap-3 border-2 border-[#101828] bg-[#25D366]/10 p-4"
              >
                <Check className="mt-0.5 h-5 w-5 flex-none text-[#25D366]" strokeWidth={3} />
                <p className="text-sm leading-relaxed text-[#101828]">
                  <strong>No import licence needed from you.</strong> No IEC,
                  no FSSAI, no BIS registration to acquire yourself — we import
                  against our own licences and deliver to any Indian address as
                  a private consignee. Perfect for individuals, dropshippers,
                  small e-commerce sellers, and first-time Japan-India shippers.
                </p>
              </div>

              {/* TL;DR */}
              <div
                id="japan-tldr"
                data-testid="japan-tldr"
                className="mt-10 border-2 border-[#101828] bg-[#FDB022] p-5 md:p-6"
              >
                <p className="font-mono-accent text-[#101828]">TL;DR</p>
                <p className="mt-2 text-base leading-relaxed text-[#101828]">
                  Sending a parcel, moving house, shipping luggage, running an
                  e-commerce store, or dropshipping from Japan to India? Custom
                  Clearance Partner is a licensed Indian Customs House Agent
                  (CHA) that clears at IGI Delhi Courier Terminal in under 6
                  hours, handles Baggage Rules 2016 TR filings, files courier
                  Bills of Entry for e-commerce, and delivers door-to-door
                  across every Indian pincode. WhatsApp{" "}
                  <a href={CONTACT.whatsappHref} className="font-bold underline">
                    +91 78003 20078
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden border-2 border-[#101828]">
                <img
                  src={heroImage}
                  alt="Air cargo aircraft — Japan to India shipping"
                  className="h-72 w-full object-cover md:h-96"
                  loading="eager"
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="border-2 border-[#101828] bg-white p-3">
                  <div className="font-display text-xl font-black leading-none text-[#101828]">
                    &lt; 6 hrs
                  </div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#475467]">
                    IGI CLEARANCE
                  </div>
                </div>
                <div className="border-2 border-[#101828] bg-[#101828] p-3">
                  <div className="font-display text-xl font-black leading-none text-[#FDB022]">
                    19,000+
                  </div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#98A2B3]">
                    INDIAN PINCODES
                  </div>
                </div>
                <div className="border-2 border-[#101828] bg-[#FDB022] p-3">
                  <div className="font-display text-xl font-black leading-none text-[#101828]">
                    24/7
                  </div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#101828]">
                    OPS DESK
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intents */}
      <section className="border-b border-[#E4E7EC] bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <p className="overline mb-4">§ What we ship from Japan to India</p>
          <h2 className="font-display text-4xl font-black leading-[0.95] tracking-tight text-[#101828] sm:text-5xl md:text-6xl">
            Five ways we handle the <span className="text-[#D92D20]">Japan–India</span> lane.
          </h2>

          <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2">
            {INTENTS.map((i, idx) => {
              const Icon = i.icon;
              return (
                <motion.article
                  key={i.id}
                  id={`intent-${i.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="bento-card flex h-full flex-col border-2 border-[#101828] bg-[#F9FAFB] p-6 md:p-8"
                  data-testid={`intent-${i.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono-accent text-[#D92D20]">{i.tag}</span>
                    <Icon className="h-6 w-6 text-[#101828]" strokeWidth={2} />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold leading-tight tracking-tight text-[#101828]">
                    {i.title}
                  </h3>
                  <p className="mt-2 font-mono-accent text-[#475467]">{i.ja}</p>
                  <p className="mt-4 text-sm leading-relaxed text-[#344054]">
                    {i.body}
                  </p>
                  <ul className="mt-5 space-y-2 border-t border-[#E4E7EC] pt-4">
                    {i.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-[#101828]">
                        <Check className="mt-0.5 h-4 w-4 flex-none text-[#D92D20]" strokeWidth={3} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 border-t border-dashed border-[#101828] pt-4">
                    <div className="font-mono-accent text-[#475467]">INDICATIVE PRICE</div>
                    <div className="mt-1 text-sm text-[#101828]">{i.priceHint}</div>
                  </div>
                  <a
                    href={CONTACT.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`intent-cta-${i.id}`}
                    className="mt-5 inline-flex items-center gap-2 font-mono-accent text-[#D92D20] hover:underline"
                  >
                    GET A QUOTE ON WHATSAPP{" "}
                    <ArrowUpRight className="h-3 w-3" strokeWidth={3} />
                  </a>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Search-friendly keyword clusters (H2/H3 keyword-rich block) */}
      <section
        id="japan-search-terms"
        data-testid="japan-search-terms"
        className="border-b border-[#E4E7EC] bg-[#F9FAFB]"
      >
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
          <p className="overline mb-4">§ Search terms our Japan-India customers use</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
            If you searched any of these — <span className="text-[#D92D20]">we're the answer.</span>
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#475467]">
            We rank for each of the terms below because we operate exactly this
            service. Every phrase is a live search query we've heard from real
            Japanese and Indian customers.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            {KEYWORD_ALIASES.map((cluster) => (
              <article
                key={cluster.slug}
                data-testid={`keyword-cluster-${cluster.slug}`}
                className="border-2 border-[#101828] bg-white p-6"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl leading-none" aria-hidden="true">
                    {cluster.icon}
                  </span>
                  <h3 className="font-display text-lg font-bold tracking-tight text-[#101828]">
                    {cluster.cluster}
                  </h3>
                </div>
                <ul className="mt-4 space-y-2 border-t border-[#E4E7EC] pt-4">
                  {cluster.items.map((it) => (
                    <li
                      key={it.term}
                      className="flex items-start gap-2 text-sm text-[#101828]"
                    >
                      <Check
                        className="mt-0.5 h-4 w-4 flex-none text-[#D92D20]"
                        strokeWidth={3}
                      />
                      <span>
                        <em className="not-italic">{it.term}</em>
                        {it.ja && (
                          <span className="font-mono-accent ml-2 text-[10px] text-[#475467]">
                            · {it.ja}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 font-mono-accent text-[#D92D20] hover:underline"
                >
                  QUOTE THIS SERVICE <ArrowUpRight className="h-3 w-3" strokeWidth={3} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Japan Post comparison strip */}
      <section
        id="japan-post-comparison"
        data-testid="japan-post-strip"
        className="border-b border-[#E4E7EC] bg-white"
      >
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
          <div className="grid grid-cols-1 gap-8 border-2 border-[#101828] bg-[#F9FAFB] p-6 md:grid-cols-12 md:p-10">
            <div className="md:col-span-8">
              <p className="font-mono-accent text-[#D92D20]">CONSIDERING JAPAN POST EMS?</p>
              <h2 className="mt-3 font-display text-2xl font-black leading-tight tracking-tight text-[#101828] sm:text-3xl md:text-4xl">
                Japan Post vs Custom Clearance Partner — read the honest comparison.
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#475467]">
                Japan Post EMS is a solid option for small personal parcels — but it ships DDU (Indian recipient pays duty on delivery), takes 10–20 days effective door-to-door, and has strict commodity restrictions. Our ₹1,200/kg all-inclusive rate covers pickup, air, customs duty, and 5-day India delivery.
              </p>
            </div>
            <div className="flex items-center justify-start md:col-span-4 md:justify-end">
              <Link
                to="/blog/japan-post-vs-custom-clearance-partner-india-shipping"
                data-testid="japan-post-blog-link"
                className="inline-flex items-center gap-2 border-2 border-[#101828] bg-[#101828] px-5 py-3 text-sm font-semibold text-white hover:bg-[#D92D20]"
              >
                Read the comparison <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lanes */}
      <section className="border-b border-[#E4E7EC] bg-[#0B111A] text-white">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
          <p className="overline mb-4 text-[#FDB022]">§ Japanese origins → Indian destinations</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl">
            Every major Japanese city — to every Indian pincode.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="border border-[#1F2937] bg-[#131C2A] p-5">
              <p className="font-mono-accent text-[#FDB022]">TOKYO REGION</p>
              <p className="mt-2 text-sm leading-relaxed text-[#98A2B3]">
                Narita (NRT), Haneda (HND), Yokohama Port → Delhi (DEL), Mumbai (BOM), Bengaluru (BLR)
              </p>
            </div>
            <div className="border border-[#1F2937] bg-[#131C2A] p-5">
              <p className="font-mono-accent text-[#FDB022]">KANSAI REGION</p>
              <p className="mt-2 text-sm leading-relaxed text-[#98A2B3]">
                Kansai / Osaka (KIX), Kobe Port → Chennai (MAA), Bengaluru (BLR), Mumbai (BOM)
              </p>
            </div>
            <div className="border border-[#1F2937] bg-[#131C2A] p-5">
              <p className="font-mono-accent text-[#FDB022]">NAGOYA / CHUBU</p>
              <p className="mt-2 text-sm leading-relaxed text-[#98A2B3]">
                Nagoya (NGO), Nagoya Port → Delhi (DEL), Chennai (MAA) for automotive lanes
              </p>
            </div>
            <div className="border border-[#1F2937] bg-[#131C2A] p-5">
              <p className="font-mono-accent text-[#FDB022]">FUKUOKA / KYUSHU</p>
              <p className="mt-2 text-sm leading-relaxed text-[#98A2B3]">
                Fukuoka (FUK) → Delhi (DEL), Mumbai (BOM) via NRT transit
              </p>
            </div>
            <div className="border border-[#1F2937] bg-[#131C2A] p-5">
              <p className="font-mono-accent text-[#FDB022]">SAPPORO / HOKKAIDO</p>
              <p className="mt-2 text-sm leading-relaxed text-[#98A2B3]">
                New Chitose (CTS) → Delhi (DEL), Mumbai (BOM) via NRT transit
              </p>
            </div>
            <div className="border border-[#1F2937] bg-[#131C2A] p-5">
              <p className="font-mono-accent text-[#FDB022]">ANY OTHER JAPANESE CITY</p>
              <p className="mt-2 text-sm leading-relaxed text-[#98A2B3]">
                Domestic feed to NRT / HND / KIX via Japanese courier + international leg
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-[#E4E7EC] bg-[#F9FAFB]">
        <div className="mx-auto max-w-4xl px-5 py-20 md:px-8 md:py-28">
          <p className="overline mb-4">§ Japan → India FAQ</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
            Straight answers.
          </h2>
          <div className="mt-10 border-2 border-[#101828] bg-white">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`jp-fq-${i}`}
                  className="border-b border-[#E4E7EC] last:border-b-0"
                  data-testid={`japan-faq-${i}`}
                >
                  <AccordionTrigger className="px-5 py-4 text-left font-display text-base font-bold tracking-tight text-[#101828] hover:no-underline md:text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 text-sm leading-relaxed text-[#475467]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-b border-[#E4E7EC] bg-[#D92D20] text-white">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center md:px-8">
          <p className="font-mono-accent text-white/80">READY TO SHIP FROM JAPAN?</p>
          <h2 className="mt-4 font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
            WhatsApp our desk with the origin, cargo type, and Indian pincode.
          </h2>
          <p className="mt-4 text-white/90">
            We reply within 15 minutes with a firm quote — 24/7, in English (Japanese summaries on request).
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="japan-bottom-whatsapp"
              className="inline-flex items-center justify-center gap-2 border-2 border-white bg-[#25D366] px-6 py-4 font-semibold text-[#0B111A] hover:bg-[#1FBE5A]"
            >
              <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
              WhatsApp +91 78003 20078
            </a>
            <a
              href={CONTACT.telHref}
              data-testid="japan-bottom-call"
              className="inline-flex items-center justify-center gap-2 border-2 border-white bg-transparent px-6 py-4 font-semibold text-white hover:bg-white hover:text-[#D92D20]"
            >
              <Phone className="h-5 w-5" strokeWidth={2.5} />
              Call the desk
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}
